// The small rules that come with what the user does, as the kit's Store applies them: every change of a to-do notes
// when it left the Inbox, whether it was decided out of it, and the series' reminder (Store.update, noteDecision,
// noteInboxExit, noteSeriesReminder); deleting an open
// Inbox item leaves a trace (traceDeletion, InboxHistory.swift); keeping a stale to-do (keep); moving a to-do between
// the Inbox, Miscellaneous, areas, projects and sections (Todo.move(to:), Store.move); a new to-do that fits its list
// (addTodo); making a to-do into a project (convertToProject). The kit is the reference.
//
// Pure: the records as they are in, the records to save or delete out (edits.ts).

import { makeID, noEdits, type Edits, type RuleContext } from "./edits";
import { markForProjectChange, markForRepeatDays, markForTodoChange } from "./featureLevel";
import { orderAfter, orderAtEnd, orderAtTop, orderBefore } from "./order";
import { makeProject, makeTodo, type Area, type InboxTrace, type Project, type Section, type Todo } from "./records";
import { sameValue } from "./records/values";
import { addDays } from "./days";
import type { RepeatRule, Start } from "./types";
import type { Route } from "./lists";

// MARK: - Every change of a to-do

/**
 * Whether a change decides an Inbox to-do (Store.decides; docs/60, Bastian, 2026-10-20: "moving out of the Inbox
 * requires deliberate action by the user"): it gets a project or area, a deadline, a reminder (a time or a place) or a
 * repeat it didn't have. A new When decides it by itself; the title, notes, checklist, tags and attachments keep it in
 * the Inbox. `old` is undefined for a to-do being added.
 */
export function decidesInbox(old: Todo | undefined, next: Todo): boolean {
  if (next.start.kind !== "inbox" || (old !== undefined && old.start.kind !== "inbox")) return false;
  const set = (key: "projectID" | "areaID" | "deadline" | "reminder" | "place" | "repeats") =>
    next[key] !== undefined && !sameValue(old?.[key], next[key]);
  return set("projectID") || set("areaID") || set("deadline") || set("reminder") || set("place") || set("repeats");
}

/** A decided Inbox to-do leaves it: for its project or area, else Miscellaneous, starting Anytime (Store.noteDecision). */
export function noteDecision(old: Todo | undefined, next: Todo): Todo {
  return decidesInbox(old, next) ? { ...next, start: { kind: "anytime" } } : next;
}

/** Leaving the Inbox leaves its date; coming back clears it (Store.noteInboxExit). */
export function noteInboxExit(old: Todo, next: Todo, now: Date): Todo {
  if (old.start.kind === "inbox" && next.start.kind !== "inbox" && next.leftInboxAt === undefined) return { ...next, leftInboxAt: now };
  if (next.start.kind === "inbox" && next.leftInboxAt !== undefined) {
    const result = { ...next };
    delete result.leftInboxAt;
    return result;
  }
  return next;
}

/**
 * Whether a change takes a to-do's reminder away (Store.losesReminder): one going to Someday had a reminder. A reminder
 * is a day and a time, Someday no day (Bastian, 2026-10-20).
 */
export function losesReminder(old: Todo, next: Todo): boolean {
  return old.start.kind !== "someday" && next.start.kind === "someday" && (old.reminder !== undefined || next.reminder !== undefined);
}

/** Going to Someday takes the reminder away (Store.noteSomeday). */
export function noteSomeday(old: Todo, next: Todo): Todo {
  if (!losesReminder(old, next) || next.reminder === undefined) return next;
  const result = { ...next };
  delete result.reminder;
  delete result.reminderZone;
  return result;
}

/**
 * Setting or changing the repeat makes the reminder of that moment the series' reminder; later changes to the
 * reminder of one occurrence leave the series as it is (Store.noteSeriesReminder).
 */
export function noteSeriesReminder(old: Todo, next: Todo): Todo {
  if (sameRule(old.repeats, next.repeats)) return next;
  const result = { ...next };
  if (next.repeats === undefined || next.reminder === undefined) delete result.seriesReminder;
  else result.seriesReminder = next.reminder;
  return result;
}

/**
 * The zones of a to-do's reminders as it is saved (Store.noteReminderZones): a reminder set or changed here was set in
 * this browser's zone, so it keeps its time on the clock wherever the user goes (docs/48). A zone given with the
 * reminder (the next one of a repeat takes the series') stays; the series' reminder made from this one gets its zone.
 */
export function noteReminderZones(old: Todo | undefined, next: Todo, here: string): Todo {
  const zone = reminderZoneFor(next.reminder, old?.reminder, next.reminderZone, old?.reminderZone, here);
  const series = next.seriesReminder !== undefined && sameMoment(next.seriesReminder, next.reminder) ? zone
    : reminderZoneFor(next.seriesReminder, old?.seriesReminder, next.seriesReminderZone, old?.seriesReminderZone, here);
  if (zone === next.reminderZone && series === next.seriesReminderZone) return next;
  const result = { ...next };
  if (zone === undefined) delete result.reminderZone;
  else result.reminderZone = zone;
  if (series === undefined) delete result.seriesReminderZone;
  else result.seriesReminderZone = series;
  return result;
}

/**
 * The zone a reminder should have (Store.zone(for:was:zone:was:here:)): none without a reminder; `here` for one that is
 * new or changed without a zone of its own; else the one it has. One from before 1.1 that nobody changes keeps none.
 */
export function reminderZoneFor(moment: Date | undefined, old: Date | undefined, zone: string | undefined, oldZone: string | undefined, here: string): string | undefined {
  if (moment === undefined) return undefined;
  if (!sameMoment(moment, old) && zone === oldZone) return here;
  return zone;
}

function sameMoment(a: Date | undefined, b: Date | undefined): boolean {
  return a === undefined || b === undefined ? a === b : a.getTime() === b.getTime();
}

/** Whether two rules are the same, as Swift compares them (weekdays as a set). */
export function sameRule(a: RepeatRule | undefined, b: RepeatRule | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  const weekdays = (rule: RepeatRule) => (rule.weekdays === undefined ? undefined : [...new Set(rule.weekdays)].sort());
  return a.interval === b.interval && a.unit === b.unit && a.afterCompletion === b.afterCompletion
    && sameValue(weekdays(a), weekdays(b))
    && a.monthPosition?.ordinal === b.monthPosition?.ordinal && a.monthPosition?.weekday === b.monthPosition?.weekday
    && sameValue(a.monthDays, b.monthDays)
    && a.endsOn === b.endsOn;
}

/** Whether two to-dos are the same record, field by field (an absent field and an undefined one alike). */
export function sameTodo(a: Todo, b: Todo): boolean {
  const defined = (todo: Todo) => Object.fromEntries(Object.entries(todo).filter(([, value]) => value !== undefined));
  return sameValue({ ...defined(a), repeats: undefined }, { ...defined(b), repeats: undefined }) && sameRule(a.repeats, b.repeats);
}

/**
 * A to-do changed by the user (Store.update(_ todo:)): nothing when it is the same; otherwise saved with its Inbox
 * exit and series reminder noted, and the account marked when the repeat got an end (older versions repeat past it).
 */
export function updateTodo(old: Todo, next: Todo, now: Date): Edits {
  const edits = noEdits();
  if (sameTodo(old, next)) return edits;
  const mark = markForTodoChange(old, next);
  if (mark) edits.marks.push(mark);
  const days = markForRepeatDays(old, next);
  if (days) edits.marks.push(days);
  edits.todos.saved.push(noteSeriesReminder(old, noteSomeday(old, noteInboxExit(old, noteDecision(old, next), now))));
  return edits;
}

/** Several to-dos changed at once (Store.update(_ changed:)): the same notes, as one change. The kit marks nothing here. */
export function updateTodos(changes: readonly { old: Todo; next: Todo }[], now: Date): Edits {
  const edits = noEdits();
  for (const { old, next } of changes) {
    if (!sameTodo(old, next)) edits.todos.saved.push(noteSeriesReminder(old, noteSomeday(old, noteInboxExit(old, noteDecision(old, next), now))));
  }
  return edits;
}

// MARK: - Deleting

/** Nothing in it: no title, notes or checklist (Todo.isEmpty). */
export function isEmptyTodo(todo: Todo): boolean {
  return todo.title.trim() === "" && todo.notes.trim() === "" && todo.checklist.length === 0;
}

/**
 * The traces deleted Inbox items leave (Store.traceDeletion): an open one that is not empty, so the Inbox's history
 * stays true. Keyed by the to-do's id; a trace there was is replaced.
 */
export function inboxTraces(deleted: readonly Todo[], now: Date): InboxTrace[] {
  return deleted
    .filter((todo) => todo.start.kind === "inbox" && todo.completedAt === undefined && !isEmptyTodo(todo))
    .map((todo) => ({ id: todo.id, createdAt: todo.createdAt, leftAt: now, unknownFields: {} }));
}

/** Deleting to-dos (Store.delete): tombstones for them, traces for the open Inbox items among them. */
export function deleteTodos(deleted: readonly Todo[], now: Date): Edits {
  const edits = noEdits();
  edits.todos.deleted = deleted.map((todo) => todo.id);
  edits.inboxTraces.saved = inboxTraces(deleted, now);
  return edits;
}

// MARK: - Stale

/**
 * Kept in the Stale review (Store.keep): the to-do stays where it is and its stale clock starts again (`keptAt`). With
 * `start`, it moves there as well, as Someday in the review does. Nothing for a completed one.
 */
export function keep(todo: Todo, now: Date, start?: Start): Edits {
  if (todo.completedAt !== undefined) return noEdits();
  const kept: Todo = { ...todo, keptAt: now };
  if (start !== undefined) kept.start = start;
  return updateTodo(todo, kept, now);
}

// MARK: - Moving

/** Where a to-do can be moved: the Inbox, Miscellaneous (no project and no area), an area or a project. */
export type MoveDestination =
  | { kind: "inbox" }
  | { kind: "general" }
  | { kind: "area"; id: string }
  | { kind: "project"; id: string };

/** Where a to-do is now (MoveDestination(of:)). */
export function destinationOf(todo: Todo): MoveDestination {
  if (todo.projectID !== undefined) return { kind: "project", id: todo.projectID };
  if (todo.areaID !== undefined) return { kind: "area", id: todo.areaID };
  return todo.start.kind === "inbox" ? { kind: "inbox" } : { kind: "general" };
}

/**
 * The to-do moved (Todo.move(to:)). Sections belong to one project, so it leaves its section when it leaves the
 * project; it leaves the Inbox (for Anytime) when it goes anywhere else, and goes back to it only when sent there.
 * Save it with `updateTodo`, which notes when it left the Inbox.
 */
export function movedTodo(todo: Todo, destination: MoveDestination): Todo {
  const project = destination.kind === "project" ? destination.id : undefined;
  const area = destination.kind === "area" ? destination.id : undefined;
  const moved: Todo = { ...todo };
  if (moved.projectID !== project) delete moved.sectionID;
  if (project === undefined) delete moved.projectID;
  else moved.projectID = project;
  if (area === undefined) delete moved.areaID;
  else moved.areaID = area;
  if (destination.kind === "inbox") moved.start = { kind: "inbox" };
  else if (moved.start.kind === "inbox") moved.start = { kind: "anytime" };
  return moved;
}

/** Moving a to-do with the Move picker: moved and saved as a change. */
export function moveTodo(todo: Todo, destination: MoveDestination, now: Date): Edits {
  return updateTodo(todo, movedTodo(todo, destination), now);
}

/**
 * Every place a to-do can move to, in the order the Move picker lists them (Store.moveDestinations): the Inbox,
 * Miscellaneous, then each area with its open projects (not one switched off), indented.
 */
export function moveDestinations(areas: readonly Area[], projects: readonly Project[]): { destination: MoveDestination; level: 0 | 1 }[] {
  const result: { destination: MoveDestination; level: 0 | 1 }[] = [{ destination: { kind: "inbox" }, level: 0 }, { destination: { kind: "general" }, level: 0 }];
  for (const area of [...areas].sort((a, b) => a.order - b.order)) {
    result.push({ destination: { kind: "area", id: area.id }, level: 0 });
    for (const project of projects.filter((p) => p.areaID === area.id && p.completedAt === undefined).sort((a, b) => a.order - b.order)) {
      if (project.isOff !== true) result.push({ destination: { kind: "project", id: project.id }, level: 1 });
    }
  }
  return result;
}

/** To the end of another section of its project (Store.move(_:toSection:)); nothing when it is there. Changes no other field. */
export function moveToSection(todo: Todo, sectionID: string | undefined, orders: Iterable<number>): Todo | null {
  if (todo.sectionID === sectionID) return null;
  const moved: Todo = { ...todo, order: orderAtEnd(orders) };
  if (sectionID === undefined) delete moved.sectionID;
  else moved.sectionID = sectionID;
  return moved;
}

/**
 * To-dos let go at the end of a section (Store.move(_:toEndOfSection:inToday:shown:in:); docs/59, item 2): after the
 * last to-do it shows (`shown`, in the list's order) other than them, in their order, so those already in it move to
 * its end too; into a section that shows no other, as moveToSection / moveToTodaySection take them. `todos` are all
 * the to-dos (for the targets and the orders). The completed to-dos the section shows at its end (not `lingering`) are
 * no place to go after: they stand in the order they were done, and in the user's order anywhere among the open ones
 * (offersPlaceBeside; Bastian, 2026-10-29). The changed to-dos; cases/lists.json ("moves").
 */
export function moveToSectionEnd(ids: readonly string[], sectionID: string | undefined, shown: readonly string[], todos: readonly Todo[], inToday: boolean, lingering?: ReadonlySet<string>): Todo[] {
  const byID = new Map(todos.map((todo) => [todo.id, todo]));
  const placedAsDone = (id: string) => byID.get(id)?.completedAt !== undefined && !(lingering?.has(id) ?? false);
  const orders = () => [...byID.values()].map((todo) => todo.order);
  const changed: Todo[] = [];
  const save = (todo: Todo) => {
    byID.set(todo.id, todo);
    const index = changed.findIndex((other) => other.id === todo.id);
    if (index >= 0) changed[index] = todo;
    else changed.push(todo);
  };
  const last = [...shown].reverse().find((id) => !ids.includes(id) && !placedAsDone(id));
  if (last !== undefined) {
    // As the kit: each right after the last, the last moved first, so they keep the order they came in.
    for (const id of [...ids].reverse()) {
      const todo = byID.get(id);
      const target = byID.get(last);
      const moved = todo && target && moveNextTo(todo, target, "after", orders(), inToday);
      if (moved) save(moved);
    }
    return changed;
  }
  for (const id of ids) {
    const todo = byID.get(id);
    const moved = todo && (inToday ? moveToTodaySection(todo, sectionID, orders()) : moveToSection(todo, sectionID, orders()));
    if (moved) save(moved);
  }
  return changed;
}

/**
 * To-dos let go on a section's heading in a list (Store.move(_:toTopOfSection:inToday:shown:in:); the owner,
 * 2026-10-19): right above the first to-do it shows (`shown`, in the list's order) other than them, in their order, so
 * those already in it move to its top too; into a section that shows no other, as moveToSection / moveToTodaySection
 * take them; never above a completed one it shows at its end (moveToSectionEnd). The changed to-dos; cases/lists.json
 * ("moves", "toTopOf").
 */
export function moveToSectionTop(ids: readonly string[], sectionID: string | undefined, shown: readonly string[], todos: readonly Todo[], inToday: boolean, lingering?: ReadonlySet<string>): Todo[] {
  const byID = new Map(todos.map((todo) => [todo.id, todo]));
  const first = shown.find((id) => !ids.includes(id) && !(byID.get(id)?.completedAt !== undefined && !(lingering?.has(id) ?? false)));
  if (first === undefined) return moveToSectionEnd(ids, sectionID, shown, todos, inToday, lingering);
  const changed = new Map<string, Todo>();
  // As the kit: each right above the first, one after another, so they keep the order they came in.
  for (const id of ids) {
    const todo = byID.get(id);
    const target = byID.get(first);
    const moved = todo && target && moveNextTo(todo, target, "before", [...byID.values()].map((other) => other.order), inToday);
    if (moved) {
      byID.set(id, moved);
      changed.set(id, moved);
    }
  }
  return [...changed.values()];
}

/** To the end of a Today section, or out of all of them (Store.move(_:toTodaySection:)). */
export function moveToTodaySection(todo: Todo, sectionID: string | undefined, orders: Iterable<number>): Todo | null {
  if (todo.todaySectionID === sectionID) return null;
  const moved: Todo = { ...todo, order: orderAtEnd(orders) };
  if (sectionID === undefined) delete moved.todaySectionID;
  else moved.todaySectionID = sectionID;
  return moved;
}

/**
 * Put right above (`before`) or below another to-do by hand (Store.move(_:before:in:), move(_:after:in:)), among all
 * the to-dos' orders. It joins that to-do's section too: the Today section in Today, the project's section elsewhere
 * when both are in the same project.
 */
export function moveNextTo(todo: Todo, target: Todo, place: "before" | "after", orders: Iterable<number>, inToday: boolean): Todo | null {
  if (todo.id === target.id) return null;
  const moved: Todo = { ...todo, order: place === "before" ? orderBefore(target.order, orders) : orderAfter(target.order, orders) };
  const join = (key: "todaySectionID" | "sectionID") => {
    if (target[key] === undefined) delete moved[key];
    else moved[key] = target[key];
  };
  if (inToday) join("todaySectionID");
  else if (todo.projectID === target.projectID) join("sectionID");
  return moved;
}

// MARK: - Projects, areas and sections

/**
 * A project changed by the user (Store.update(_ project:)): saved, the account marked when it first repeats (older
 * versions complete it without its next copy). A move into an area `canMoveTo` refuses (a project shared on its own
 * into a shared area: sharing.ts, `canMove`) keeps the old area; the rest of the change goes through.
 */
export function updateProject(old: Project, next: Project, canMoveTo: (areaID: string) => boolean = () => true): Edits {
  const edits = noEdits();
  const mark = markForProjectChange(old, next);
  if (mark) edits.marks.push(mark);
  const days = markForRepeatDays(old, next);
  if (days) edits.marks.push(days);
  const project: Project = { ...next };
  if (next.areaID !== undefined && next.areaID !== old.areaID && !canMoveTo(next.areaID)) {
    if (old.areaID === undefined) delete project.areaID;
    else project.areaID = old.areaID;
  }
  edits.projects.saved.push(project);
  return edits;
}

/**
 * The records renumbered 1, 2, 3… in the order given, with `id` taken out and put before `before` (last when absent):
 * only those whose order changes are returned (Store.moveArea, moveProject, moveSection(before:)).
 */
function renumbered<T extends { id: string; order: number }>(sorted: readonly T[], moving: T, before: string | undefined): T[] {
  const order = sorted.filter((record) => record.id !== moving.id);
  const position = before === undefined ? -1 : order.findIndex((record) => record.id === before);
  order.splice(position === -1 ? order.length : position, 0, moving);
  return order.map((record, index) => (record.order === index + 1 ? record : { ...record, order: index + 1 }))
    .filter((record, index) => record !== order[index]);
}

/** An area put where another one is, pushing that one and those after it down; last without `before`. */
export function moveArea(moving: Area, before: string | undefined, areas: readonly Area[]): Area[] {
  if (moving.id === before) return [];
  return renumbered([...areas].sort((a, b) => a.order - b.order), moving, before);
}

/**
 * A project put right above another one of an area (`before`), or at its end, moving it into that area if need be
 * (Store.moveProject). Null when the move is refused (`canMoveTo`: a project shared on its own into a shared area).
 * Only the projects whose place or area changes are returned.
 */
export function moveProject(
  moving: Project, before: string | undefined, areaID: string, projects: readonly Project[], canMoveTo: (areaID: string) => boolean = () => true,
): Project[] | null {
  if (moving.id === before || !canMoveTo(areaID)) return null;
  const inArea = projects.filter((project) => project.areaID === areaID && project.completedAt === undefined && project.id !== moving.id)
    .sort((a, b) => a.order - b.order);
  const position = before === undefined ? -1 : inArea.findIndex((project) => project.id === before);
  inArea.splice(position === -1 ? inArea.length : position, 0, moving);
  return inArea.flatMap((project, index) => {
    if (project.order === index + 1 && project.areaID === areaID) return [];
    return [{ ...project, order: index + 1, areaID }];
  });
}

/** A section put right before another one of the same project (or of Today), or last (Store.moveSection(_:before:)). */
export function moveSection(moving: Section, before: string | undefined, sections: readonly Section[]): Section[] {
  if (moving.id === before) return [];
  const siblings = sections.filter((section) => section.projectID === moving.projectID).sort((a, b) => a.order - b.order);
  return renumbered(siblings, moving, before);
}

/** A new section at the end of a project, or of Today without one (Store.addSection): after every section's order. */
export function newSection(title: string, projectID: string | undefined, sections: readonly Section[], context: Pick<RuleContext, "newID">): Section {
  const section: Section = { id: makeID(context), title, order: orderAtEnd(sections.map((other) => other.order)), unknownFields: {} };
  if (projectID !== undefined) section.projectID = projectID;
  return section;
}

/** Deleting a section keeps its to-dos: in the project, out of the section (and out of it in Today) (Store.deleteSection). */
export function deleteSection(section: Section, todos: readonly Todo[]): Edits {
  const edits = noEdits();
  edits.sections.deleted.push(section.id);
  for (const todo of todos) {
    if (todo.sectionID !== section.id && todo.todaySectionID !== section.id) continue;
    const kept: Todo = { ...todo };
    if (kept.sectionID === section.id) delete kept.sectionID;
    if (kept.todaySectionID === section.id) delete kept.todaySectionID;
    edits.todos.saved.push(kept);
  }
  return edits;
}

// MARK: - New to-dos

/**
 * A new to-do that fits the list it was added from (Store.addTodo): its start, its project, area or deadline from
 * the list; on top of everything, at the end (`atEnd`), or right below `after`. In Today, `sectionID` is a Today
 * section; elsewhere a project's section.
 */
export function newTodo(
  route: Route, orders: readonly number[], context: Pick<RuleContext, "today" | "now" | "newID">,
  options: { sectionID?: string; atEnd?: boolean; after?: Todo } = {},
): Todo {
  const todo = makeTodo({ id: makeID(context), createdAt: context.now });
  if (options.sectionID !== undefined) {
    if (route.kind === "today") todo.todaySectionID = options.sectionID;
    else todo.sectionID = options.sectionID;
  }
  switch (route.kind) {
    case "today": todo.start = { kind: "on", day: context.today }; break;
    case "scheduled": todo.start = { kind: "on", day: addDays(context.today, 1) }; break;
    case "anytime": case "general": todo.start = { kind: "anytime" }; break;
    case "due": todo.start = { kind: "anytime" }; todo.deadline = context.today; break;
    case "someday": todo.start = { kind: "someday" }; break;
    case "project": todo.start = { kind: "anytime" }; todo.projectID = route.id; break;
    case "area": todo.start = { kind: "anytime" }; todo.areaID = route.id; break;
    // The Inbox, the Archive, Find, Stale, templates: the Inbox. (A place's list, where the iPhone adds the place's
    // reminder, doesn't exist on the web.)
    default: todo.start = { kind: "inbox" }; break;
  }
  todo.order = options.after ? orderAfter(options.after.order, orders) : options.atEnd ? orderAtEnd(orders) : orderAtTop(orders);
  return todo;
}

// MARK: - Making a to-do into a project

/** The area a project made from this to-do would belong to, if that is clear already (Store.areaForProject); else ask. */
export function areaForProject(todo: Todo, areas: readonly Area[], projects: readonly Project[]): Area | undefined {
  const byID = (id: string | undefined) => (id === undefined ? undefined : areas.find((area) => area.id === id));
  return byID(todo.areaID) ?? byID(projects.find((project) => project.id === todo.projectID)?.areaID);
}

/** Where a project made from a to-do goes (Store.projectAreaChoice / ProjectAreaChoice). */
export type ProjectAreaChoice = { kind: "area"; area: Area } | { kind: "choose"; areas: Area[] } | { kind: "unavailable" };

/**
 * What Convert to Project does with this to-do (Store.projectAreaChoice; Bastian, 2026-10-20): its own area at once;
 * without one, a choice of every area the person may make a project in, in the sidebar's order (`sortedAreas`). A
 * shared area's projects only its owner makes (the Firestore rules), so a to-do in someone else's shared area is not
 * converted, and their areas are not offered (`mayAddProject`).
 */
export function projectAreaChoice(
  todo: Todo, sortedAreas: readonly Area[], projects: readonly Project[], mayAddProject: (area: Area) => boolean,
): ProjectAreaChoice {
  const own = areaForProject(todo, sortedAreas, projects);
  if (own) return mayAddProject(own) ? { kind: "area", area: own } : { kind: "unavailable" };
  const areas = sortedAreas.filter(mayAddProject);
  return areas.length > 0 ? { kind: "choose", areas } : { kind: "unavailable" };
}

/**
 * Turns a to-do into a project in `area` (Store.convertToProject): its title, notes and deadline; Someday stays
 * Someday, a later day becomes the project's start, anything else starts now. Its open checklist items become the
 * project's first to-dos, at the end; the to-do itself is deleted (with its Inbox trace, when it was an Inbox item).
 */
export function convertToProject(
  todo: Todo, area: Area, data: { projects: readonly Project[]; todos: readonly Todo[] }, context: RuleContext,
): { edits: Edits; project: Project } {
  const edits = noEdits();
  const project = makeProject({
    id: makeID(context), title: todo.title, notes: todo.notes, areaID: area.id, order: orderAtEnd(data.projects.map((p) => p.order)),
  });
  if (todo.deadline !== undefined) project.deadline = todo.deadline;
  if (todo.start.kind === "someday") project.start = { kind: "someday" };
  else if (todo.start.kind === "on" && todo.start.day > context.today) project.start = { kind: "on", day: todo.start.day };
  edits.projects.saved.push(project);
  const orders = data.todos.map((other) => other.order);
  for (const item of todo.checklist.filter((entry) => !entry.isDone)) {
    const step = makeTodo({
      id: makeID(context), title: item.title, start: { kind: "anytime" }, projectID: project.id, order: orderAtEnd(orders), createdAt: context.now,
    });
    orders.push(step.order);
    edits.todos.saved.push(step);
  }
  const deletion = deleteTodos([todo], context.now);
  edits.todos.deleted = deletion.todos.deleted;
  edits.inboxTraces.saved = deletion.inboxTraces.saved;
  return { edits, project };
}

// MARK: - Loading

/**
 * Projects whose area doesn't exist go into the first area, or a new one when there is none (Store.adoptLooseProjects,
 * when the account is loaded): every project lives in an area. `title` is the new area's name in the user's
 * content language ("Projects").
 */
export function adoptLooseProjects(projects: readonly Project[], areas: readonly Area[], title: string, context: Pick<RuleContext, "newID">): Edits {
  const edits = noEdits();
  const exists = (id: string | undefined) => id !== undefined && areas.some((area) => area.id === id);
  const loose = projects.filter((project) => !exists(project.areaID));
  if (loose.length === 0) return edits;
  let home = [...areas].sort((a, b) => a.order - b.order)[0];
  if (!home) {
    home = { id: makeID(context), title, order: orderAtEnd([]), isCollapsed: false, unknownFields: {} };
    edits.areas.saved.push(home);
  }
  const areaID = home.id;
  edits.projects.saved = loose.map((project) => ({ ...project, areaID }));
  return edits;
}
